import * as React from 'react';
import * as styles from './FeedStructureViewer.css';
import { LocalizationService } from '../../services/LocalizationService';
import { IFeed, IFeedStructureItem } from '../../interfaces/IFeed';
import FeedService from '../../services/FeedService';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IASkeleton } from '../skeleton/Skeleton';
import { IACheckbox } from '../checkbox/Checkbox';
import { IAIcon } from '../icon/Icon';

export interface IProps {
  environment: IEnvironment;
  tenant: string
  application: "messages" | "events";
  highlightColor: string;
  availableFeeds: IFeed[];
  selectedFeedIds: string[];
  instanceName: string;
  onFeedSelectedChange: (feed: IFeed, isChecked: boolean) => void;
}

export interface IState {
  isLoadingFeedStructure: boolean;
  structuredFeeds: IFeedStructureItemWithCollapse[];
  feedIdsFromStructure: string[];
  feedsOutsideStructure: IFeed[];
}

interface IFeedStructureItemWithCollapse extends IFeedStructureItem {
  isChildrenVisible: boolean;
}

export class FeedStructureViewer extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      isLoadingFeedStructure: false,
      structuredFeeds: [],
      feedIdsFromStructure: [],
      feedsOutsideStructure: []
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }
  
  componentDidMount(): void {
    this.setState({ isLoadingFeedStructure: true });
    FeedService.getFeedStructure(this.props.environment, this.props.tenant, this.props.application)
    .then(structuredFeeds => {
      if (structuredFeeds?.length > 0) {
        // only show headlines and feeds that have the current message/event instance
        this.setState({ structuredFeeds: this.recursivelyFilterFeedsOnInstance(structuredFeeds) }, () => {
          this.setState({ feedsOutsideStructure: this.props.availableFeeds.filter(feed => !this.state.feedIdsFromStructure.some(structuredFeedId => structuredFeedId === feed.id)) });
        });
      } else {
        this.setState({ feedsOutsideStructure: this.props.availableFeeds});
      }
    })
    .catch(error => {
      if (error === 404) {
        // structure not set up yet - add everything to a flat structure
        this.setState({ feedsOutsideStructure: this.props.availableFeeds});
      }
    })
    .finally(() => {
      this.setState({ isLoadingFeedStructure: false });
    })
  }

  private getSkeleton(): JSX.Element {
    return <>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "60%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "80%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "70%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "50%",
          borderRadius: 5,
        }}
        />
      </div>
    </>
  }

  private recursivelyFilterFeedsOnInstance(structuredFeeds: IFeedStructureItem[], treeDepth = 0): IFeedStructureItemWithCollapse[] {
    return structuredFeeds.map(feedStructureItem => {
      const item: IFeedStructureItemWithCollapse = {
        ...feedStructureItem,
        isChildrenVisible: treeDepth <= 1 ? true : false
      }
      return item;
    }).filter(feedStructureItem => {
      if (feedStructureItem.id) {
        const feed = this.props.availableFeeds.find(f => f.id === feedStructureItem.id);
        if (!feed?.instances.some(instance => instance === this.props.instanceName)) {
          // this feed doesn't contain our current instance, so we filter it away
          return false;
        }
      } else if (feedStructureItem.children?.length > 0) {
        feedStructureItem.children = this.recursivelyFilterFeedsOnInstance(feedStructureItem.children, treeDepth + 1);
        if (feedStructureItem.children.length === 0) {
          // headlines where all children are filtered away will be removed
          return false;
        }
      }
      this.setState({ feedIdsFromStructure: [...this.state.feedIdsFromStructure, feedStructureItem.id ]});
      return true;
    });
  }

  private getCheckbox(structuredFeed: IFeedStructureItemWithCollapse, margin: number = 0): JSX.Element {
    const marginIncrement = 20;
    return <>
      <div
        className={styles.IA_checkboxWrapper}
        style={{ marginLeft: marginIncrement + margin }}
      >
        <IAIcon
          color="grey"
          size={12}
          url={structuredFeed.isChildrenVisible 
            ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"
            : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Right/SVG/ic_fluent_chevron_right_48_regular.svg"
          }
          style={{
            display: structuredFeed.children?.length > 0 ? "block" : "none"
          }}
          onClick={() => {
            structuredFeed.isChildrenVisible = !!!structuredFeed.isChildrenVisible;
            this.forceUpdate();
          }}
        />
        <IACheckbox
          styles={{
            marginLeft: 10,
          }}
          inputStyle={{
            borderRadius: 3
          }}
          labelStyles={{
            fontWeight: structuredFeed.highlighted ? "bold" : undefined
          }}
          highlightColor={this.props.highlightColor}
          onChange={(isChecked) => {
            if (structuredFeed.id) {
              // user clicked directly on a feed
              this.props.onFeedSelectedChange(this.props.availableFeeds.find(feed => feed.id === structuredFeed.id), isChecked);
            } else {
              // user clicked on a header, so we find all children and check them
              this.findChildren(structuredFeed).forEach(child => this.props.onFeedSelectedChange(this.props.availableFeeds.find(feed => feed.id === child.id), isChecked));
            }
          }}
          label={structuredFeed.title}
          isChecked={this.isChecked(structuredFeed)}
          marked={this.isPartlyChecked(structuredFeed)}
        />
      </div>
      <div
        style={{
          display: structuredFeed.isChildrenVisible ? "block" : "none"
        }}
      >
        {structuredFeed.children?.length > 0 && structuredFeed.children.map(child => this.getCheckbox(child as IFeedStructureItemWithCollapse, margin + marginIncrement))}
      </div>
    </>
  }

  private isChecked(structuredFeed: IFeedStructureItem): boolean {
    if (structuredFeed.id) {
      return this.props.selectedFeedIds.some(id => id === structuredFeed.id);
    }

    // check if all children are checked
    if (structuredFeed.children?.length > 0) {
      return !structuredFeed.children?.map(child => this.isChecked(child)).some(feed => feed === false);
    }
    return false;
  }

  private isPartlyChecked(structuredFeed: IFeedStructureItem): boolean {
    if (!structuredFeed.id && structuredFeed.children?.length > 0) {
      return structuredFeed.children?.map(child => this.isChecked(child)).some(feed => feed === true) || structuredFeed.children?.map(child => this.isPartlyChecked(child)).some(feed => feed === true);
    }
    return false;
  }

  private findChildren(selectedFeedStructureItem: IFeedStructureItem, childrenFound: IFeedStructureItem[] = []): IFeedStructureItem[] {
    if (selectedFeedStructureItem.id) {
      childrenFound.push(selectedFeedStructureItem);
      return childrenFound;
    }
    if (selectedFeedStructureItem.children?.length > 0) {
      selectedFeedStructureItem.children.forEach(child => {
        if (child.id) {
          childrenFound.push(child);
        } else {
          child.children?.forEach(grandChild => this.findChildren(grandChild, childrenFound));
        }
      });
    }
    return childrenFound;
  }

  public render(): JSX.Element {
    return (
      <>
        {this.state.isLoadingFeedStructure && this.getSkeleton()}
        {(this.state.structuredFeeds?.length > 0 || this.state.feedsOutsideStructure?.length > 0) &&
          <>
            <IACheckbox
              styles={{
                marginTop: 10
              }}
              inputStyle={{
                borderRadius: 3
              }}
              highlightColor={this.props.highlightColor}
              onChange={(isChecked) => {
                this.state.structuredFeeds.forEach(structuredFeed => {
                  this.findChildren(structuredFeed).forEach(child => this.props.onFeedSelectedChange(this.props.availableFeeds.find(feed => feed.id === child.id), isChecked));
                });
                this.state.feedsOutsideStructure.forEach(feed => this.props.onFeedSelectedChange(feed, isChecked));
              }}
              label={this.localizationService.strings.TargetTree_All}
              // isChecked if all children are checked
              isChecked={this.state.structuredFeeds.map(feed => this.isChecked(feed)).reduce((previous, current) => previous && current, true) && this.state.feedsOutsideStructure.map(feed => this.isChecked(feed)).reduce((previous, current) => previous && current, true)}
            />
            {this.state.structuredFeeds.map(structuredFeed => this.getCheckbox(structuredFeed))}
            {this.state.feedsOutsideStructure.map(feed => this.getCheckbox({
              isChildrenVisible: true,
              feed: feed,
              title: feed.title,
              id: feed.id
            }))}
          </>
        }
      </>
    );
  }
}